import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import LoadingOverlay from './LoadingOverlay';
import debug from '../../utils/debug';

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingSource, setLoadingSource] = useState("");

  // Use ref to avoid stale closure in hideLoading
  const loadingSourceRef = useRef("");

  const showLoading = useCallback((message = "", source = "") => {
    debug.log('LoadingContext', `Loading started: ${source} - ${message}`);
    loadingSourceRef.current = source;
    setIsLoading(true);
    setLoadingMessage(message);
    setLoadingSource(source);
  }, []);

  const hideLoading = useCallback((source = "") => {
    if (!source || source === loadingSourceRef.current) {
      debug.log('LoadingContext', `Loading ended: ${loadingSourceRef.current}`);
      loadingSourceRef.current = "";
      setIsLoading(false);
      setLoadingMessage("");
      setLoadingSource("");
    } else {
      debug.log('LoadingContext', `Loading hide ignored - different source (current: ${loadingSourceRef.current}, requested: ${source})`);
    }
  }, []);

  const contextValue = useMemo(() => ({
    isLoading,
    loadingMessage,
    showLoading,
    hideLoading,
    setIsLoading,
    loadingSource
  }), [isLoading, loadingMessage, showLoading, hideLoading, loadingSource]);

  return (
    <LoadingContext.Provider value={contextValue}>
      {children}
      {isLoading && (
        <LoadingOverlay
          message={loadingMessage || "Loading..."}
          showProgress={false}
        />
      )}
    </LoadingContext.Provider>
  );
};

export default LoadingContext;
